import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/utils/format/currency";
import { Dialog, Transition } from "@headlessui/react";
import { ArrowRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Link } from "react-router-dom";
import { StatusBadge } from "../shared/status-badge";
import { BookingDetailsProps } from "./booking-details.types";
import { formatInTimeZone } from "date-fns-tz";

export const BookingDetails = ({
  open,
  handleCloseModal,
  detail,
}: BookingDetailsProps) => {
  const formatBookingDetail = (bookingDetail: string) => {
    if (bookingDetail === "") return "-";

    let [startDate, endDate] = bookingDetail.split(" - ");

    startDate = formatInTimeZone(startDate, "UTC", "dd/MM/yyyy");
    endDate = formatInTimeZone(endDate, "UTC", "dd/MM/yyyy");

    return (
      <span className="inline-flex items-center gap-2">
        {startDate}
        <ArrowRightIcon className="w-4 h-4" />
        {endDate}
      </span>
    );
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleCloseModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 ">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto relative w-screen max-w-2xl ">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-500"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-500"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-0 top-0 -ml-8 flex pr-2 pt-4 sm:-ml-10 sm:pr-4">
                      <button
                        type="button"
                        className="relative rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                        onClick={handleCloseModal}
                      >
                        <span className="absolute -inset-2.5" />
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                  </Transition.Child>
                  <div className="flex h-full flex-col overflow-y-scroll bg-white w-full py-6 shadow-xl">
                    <div className="px-4 sm:px-6">
                      <Dialog.Title className="text-base font-semibold leading-6 text-zinc-950">
                        Detalhes booking
                      </Dialog.Title>
                      <div className="border-[1px] border-gray-100 rounded-md overflow-hidden my-8">
                        <div className="bg-slate-100 p-2 flex">
                          Private room booking
                          <p className="font-bold ml-2">#{detail?.id}</p>
                        </div>
                        <div className="p-4">
                          <ul>
                            <li className=" py-2">
                              <span className="font-bold">
                                ID do contrato:{" "}
                              </span>
                              {detail?.po_contract_id
                                ? `#${detail?.po_contract_id}`
                                : "---"}
                            </li>
                            <li className=" py-2">
                              <span className="font-bold">Data: </span>
                              {formatBookingDetail(
                                detail?.booking_detail || "",
                              )}
                            </li>
                            <li className=" py-2">
                              <span className="font-bold">Capacidade: </span>
                              {detail?.privateRooms?.capacity}
                            </li>
                            <li className=" py-2">
                              <span className="font-bold">Créditos: </span>
                              {detail?.credits}
                            </li>
                            <li className=" py-2">
                              <span className="font-bold">Status: </span>
                              <StatusBadge
                                status={detail?.status as number}
                                type="STATUS_BOOKINGS"
                              />
                            </li>
                            <li className=" py-2">
                              <span className="font-bold">Membros: </span>
                              {detail?.privateRooms?.capacity}
                            </li>
                            <li className=" py-2">
                              <span className="font-bold ">Empresa: </span>
                              {detail?.companies.name} - {detail?.companies.id}
                            </li>
                            <li className=" py-2">
                              <span className="font-bold ">Grupo: </span>
                              {detail?.group_id ? detail?.group_id : "---"}
                            </li>

                            <li className=" py-2">
                              <span className="font-bold">Sala privada: </span>
                              {detail?.privateRooms?.label} -{" "}
                              {detail?.privateRooms?.id}
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="border-[1px] border-yellow-500 rounded-md overflow-hidden my-8">
                        <div className="bg-yellow-500 p-2 flex">Coworking</div>

                        <div className="p-4">
                          <ul>
                            <li>
                              <span className="font-bold">Espaço: </span>
                              {detail?.spaces?.vanish_name ? (
                                <Button
                                  variant="link"
                                  className="p-0 underline"
                                  asChild
                                >
                                  <Link
                                    to={`${import.meta.env.VITE_PORTAL_URL}/coworking/space/${detail.spaces.vanish_name}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {detail.spaces.name} - {detail.spaces.id}
                                  </Link>
                                </Button>
                              ) : (
                                <span>
                                  {detail?.spaces?.name} - {detail?.spaces?.id}
                                </span>
                              )}
                            </li>
                            <li className="py-2">
                              <span className="font-bold">Endereço: </span>
                              {detail?.spaces.address}
                            </li>
                            <li className="py-2">
                              <span className="font-bold">
                                CM Responsável:{" "}
                              </span>
                              {detail?.spaces.cm_name}
                            </li>
                            <li className="py-2">
                              <span className="font-bold">CM E-mail: </span>
                              {detail?.spaces.cm_email}
                            </li>
                            <li className="py-2">
                              <span className="font-bold">CM Telefone: </span>
                              {detail?.spaces.cm_phone}
                            </li>
                          </ul>
                        </div>
                      </div>

                      <div className="border-[1px] border-x-yellow-950 rounded-md overflow-hidden my-8">
                        <div className="bg-black p-2 flex text-white">
                          Financeiro
                        </div>

                        <div className="p-4">
                          <ul>
                            <li className=" py-2">
                              <span className="font-bold">
                                Créditos mensais:{" "}
                              </span>
                              {detail?.credits}
                            </li>
                            <li className=" py-2">
                              <span className="font-bold">take: </span>
                              {
                                detail?.bookingAttributes
                                  .booking_payout_rate_perc
                              }
                              %
                            </li>
                            <li className=" py-2">
                              <span className="font-bold">Repasse: </span>

                              {formatCurrency(
                                Number(
                                  detail?.bookingAttributes.booking_payout,
                                ) || 0,
                              )}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="relative mt-6 flex-1 px-4 sm:px-6">
                      {/* Your content */}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
